import { select } from '@ngrx/store';
import { Observable, zip } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import * as moment from 'moment';
import { TranslationService } from '@cemex-core/angular-localization-v7';
import { PaymentHeader } from '@cemex-core/types-v1';

import { AbstractPaymentEngine } from './abstract-payment-engine';
import { PaymentConfiguration } from './../models/payment-configuration';
import { PaymentEngineConfiguration } from './payment-engine-configuration';
import { PaymentRequest } from './../models/payment-request';
import { PaymentProcessActions } from './../store/actions/payment-process.action';
import { PayerBrokerRequest } from './../models/payer-broker-request';
import { PayerBrokerResponse } from './../models/payer-broker-response';
import * as fromPayments from './../store/reducers/index';

export class PaymentEngineMX extends AbstractPaymentEngine {

    constructor(configuration: PaymentEngineConfiguration, t: TranslationService) {
        super(configuration, t);
    }

    getPaymentConfiguration(): Observable<PaymentConfiguration> {
        return zip(
            super.initPaymentConfiguration(),
            this.bankService.getBankList({
                companyCode: this.company.companyCode,
                countryCode: this.customer.countryCode,
                customerCode: this.customer.customerCode
            })
        ).pipe(
            map(([paymentConfiguration, banks]) => {
                banks.forEach(bank => bank.bankCode = bank.shortID4HouseBank);
                paymentConfiguration.paymentMethods.forEach(pm => {
                    if (pm.code === 'REFERENCE' && banks.filter(bank => bank.bankCode === 'STD01').length > 0) {
                        pm.paymentNetworks = banks.filter(bank => bank.bankCode === 'STD01');
                    } else {
                        pm.paymentNetworks = banks;
                    }
                });

                return paymentConfiguration;
            })
        );
    }

    getBanks(): any { }

    createReference(request: PaymentRequest): Observable<any> {
        return this.paymentService.createReferenceV2(request).pipe(
            map(response => {
                this.store.dispatch(new PaymentProcessActions.GetPayerInfomationSuccess(response.parameters));

                return response.headerOut[0];
            })
        );
    }

    getPayerBroker(request: PayerBrokerRequest, brokerCode: string): Observable<any> {
        return this.store.pipe(
            select(fromPayments.getPayerInformation),
            filter(payerInformation => payerInformation !== undefined),
            map(payerInformation => {
                switch (brokerCode.trim()) {
                    case 'BMR01':
                        return this.getBancomerPayerInformation(request, payerInformation);
                        break;
                    case 'STD01':
                            return this.getSantanderPayerInformation(request, payerInformation);
                        break;
                    case 'BMX01':
                            return this.getBanamexPayerInformation(request, payerInformation);
                        break;
                }
            })
        );
    }

    callToNetworking(totalToPay: string, referenceCode: string, payerInformation: PayerBrokerResponse): void {
        const formParams = payerInformation.formParams;
        const action = formParams.find(x => x.key === 'endpoint').value;
        const method = formParams.find(x => x.key === 'method').value;
        const parameters = {};

        formParams.filter(x => x.key !== 'endpoint' && x.key !== 'method').forEach(param => {
            parameters[param.key] = param.value;
        });

        this.createFormRequestNetwork(action, method, '_blank', parameters);
    }

    getBancomerPayerInformation(request: PayerBrokerRequest, payerInformation: any): PayerBrokerResponse {
        const response: PayerBrokerResponse = { formParams: [] };
        const concepto = payerInformation.find(x => x.parameter === 'concepto').value;
        const numeroconvenio = payerInformation.find(x => x.parameter === 'numeroconvenio').value;
        const empresa = payerInformation.find(x => x.parameter === 'empresa').value;
        const fecha = moment(new Date()).format('YYYY-MM-DD');

        response.formParams.push({ key: 'endpoint', value: window['BMR_URL'] });
        response.formParams.push({ key: 'method', value: 'GET' });
        response.formParams.push({ key: 'referencia', value: request.referenceCode });
        response.formParams.push({ key: 'concepto', value: concepto });
        response.formParams.push({ key: 'fecha', value: fecha });
        response.formParams.push({ key: 'importe', value: request.amount });
        response.formParams.push({ key: 'numeroconvenio', value: numeroconvenio });
        response.formParams.push({ key: 'empresa', value: empresa });
        response.formParams.push({ key: 'regreso', value: window['BMR_FB_URL'] });

        return response;
    }

    getSantanderPayerInformation(request: PayerBrokerRequest, payerInformation: any): PayerBrokerResponse {
        const response: PayerBrokerResponse = { formParams: [] };
        const numeroconvenio = payerInformation.find(x => x.parameter === 'convenio').value;

        response.formParams.push({ key: 'endpoint', value: window['STD_URL'] });
        response.formParams.push({ key: 'method', value: 'GET' });
        response.formParams.push({ key: 'convenio', value: numeroconvenio });
        response.formParams.push({ key: 'url_resp', value: window['STD_FB_URL'] });
        response.formParams.push({ key: 'importe', value: request.amount });
        response.formParams.push({ key: 'referencia', value: request.referenceCode });

        return response;
    }

    getBanamexPayerInformation(request: PayerBrokerRequest, payerInformation: any): PayerBrokerResponse {
        const response: PayerBrokerResponse = { formParams: [] };

        const cliente = payerInformation.find(x => x.parameter === 'strCliente').value;
        const dominio = window['BMX_FB_URL'];
        const tipoabono = payerInformation.find(x => x.parameter === 'strTipoAbono').value;
        const importe = (parseFloat(parseFloat(request.amount).toFixed(2)) * 100).toFixed(0);
        const fecha = moment(new Date()).format('YYYY-MM-DD');
        const concepto = payerInformation.find(x => x.parameter === 'strConcepto').value;
        const referencia = request.referenceCode;
        const extra2 = [
            cliente,
            dominio,
            tipoabono,
            importe,
            fecha,
            concepto,
            referencia
        ];

        response.formParams.push({ key: 'endpoint', value: window['BMX_URL_P'] });
        response.formParams.push({ key: 'method', value: 'POST' });
        response.formParams.push({ key: 'EWF_SYS_0', value: '4eebd5b1-3824-11d5-929d-0050dae9973a' });
        response.formParams.push({ key: 'EXTRA1', value: 'SPANISH' });
        response.formParams.push({ key: 'EXTRA3', value: '' });
        response.formParams.push({ key: 'EXTRA4', value: 'NO_ERROR' });
        response.formParams.push({ key: 'LANGUAJEID', value: '1' });
        response.formParams.push({ key: 'EWF_FORM_NAME', value: 'index' });
        response.formParams.push({ key: 'PRUDUCTNAME', value: 'EBS' });
        response.formParams.push({ key: 'EWFBUTTON', value: '' });
        response.formParams.push({ key: 'EXTRA2', value: extra2.join('|') });

        return response;
    }

    saveExternalOrderRelation(): any {
        throw new Error('Method not implemented.');
    }

    getHeaderPayerInformation(request: any, payerInformationResponse: any, storeDataInfo: any): any {
        throw new Error('Method not implemented.');
    }

}
